/**
 * useApptimize React hook
 *
 * Integrates the Apptimize A/B testing and feature flag SDK into the app.
 * Handles initialization, user association, and feature flag updates.
 *
 * @returns {{
 *   isApptimizeInitialized: boolean
 * }}
 * isApptimizeInitialized - Whether Apptimize SDK has been initialized
 */

import Apptimize from '@apptimize/apptimize-web-sdk'
import config from 'const/config'
import { useCreditAccounts } from 'const/syfApiQueries'
import {
  completeApptimizeInitialization,
  timeoutApptimizeInitialization
} from 'helpers/apptimizeInitialization'
import { useEffect, useState } from 'react'
import updateFeatureFlags from 'services/updateFeatureFlags'
import { useSessionStorageValue } from 'utils/sessionStorageWithEvents'

export function useApptimize(setIsFeatureFlagsFetched) {
  /*
   * State to track Apptimize SDK initialization
   * Access Apptimize.isFeatureFlagEnabled only after initialization to avoid errors in safari/firefox
   */
  const [isApptimizeInitialized, setIsApptimizeInitialized] = useState(false)

  const subject = useSessionStorageValue('subject')
  const { isError: isCreditAccountsAPIError } = useCreditAccounts()

  // Initialize Apptimize SDK on mount
  useEffect(() => {
    const apptimizeTimeout = setTimeout(() => {
      timeoutApptimizeInitialization()
    }, 5000)

    Apptimize.setOnApptimizeInitializedCallback(() => {
      clearTimeout(apptimizeTimeout)
      Apptimize.track('Apptimize Initialized')
      setIsApptimizeInitialized(true)
      completeApptimizeInitialization()
    })
    // refresh_metadata_on_setup - to always download metadata on setup
    Apptimize.setup(config.APPTIMIZE_APP_KEY, {
      refresh_metadata_on_setup: true
    })

    const flushApptimizeEvents = () => {
      Apptimize.flushTracking()
    }

    window.addEventListener('beforeunload', flushApptimizeEvents)

    return () => {
      window.removeEventListener('beforeunload', flushApptimizeEvents)
    }
  }, [])

  // Set user for Apptimize and update feature flags when initialized or subject changes
  useEffect(() => {
    if (!isApptimizeInitialized || !subject) return
    // If the current Apptimize user is not the subject, update it
    const currentUserId = Apptimize.getCustomerUserId()
    if (currentUserId !== subject) {
      Apptimize.setCustomerUserId(subject)
    }
    /*
     * call updateFeatureFlags after Apptimize is initialized and subject is available
     * to ensure that same user gets the same variant in all devices/browsers.
     * When Apptimize is initialized and subject is not available, Apptimize will assign a anonymous customerUserId.
     * And return variants for the anonymous customerUserId.
     * When subject is available, we update it as customerUserId and get the variants for that User.
     */
    updateFeatureFlags(setIsFeatureFlagsFetched, isApptimizeInitialized)
    // Ensures the Apptimize pilot ID matches the current subject to keep targeting consistent
    const pilotTargetingId = Apptimize.getPilotTargetingId()
    if (pilotTargetingId !== subject) {
      Apptimize.setPilotTargetingId(subject)
    }

    // Expose Apptimize feature flags to global window object for debugging/analytics
    interface WindowWithSFDDL extends Window {
      _SFDDL: Record<string, unknown>
    }
    const { _SFDDL: SFDDL } = window as WindowWithSFDDL
    const slowRolloutFeatureFlags = Apptimize.getStringArray(
      'slowRolloutFeatureFlags'
    )
    const slowRolloutFeatures = slowRolloutFeatureFlags?.reduce(
      (acc, flag) => ({ ...acc, [flag]: Apptimize.isFeatureFlagEnabled(flag) }),
      {}
    )

    let abTesting: string | undefined

    const lwsRedirectUri = sessionStorage.getItem('lwsRedirectUri')
    const isLwsRedirect = !!lwsRedirectUri

    if (
      !isLwsRedirect &&
      !isCreditAccountsAPIError &&
      Apptimize.isFeatureFlagEnabled('isAddAccountABTestingEnabled')
    ) {
      const addAccountVariant = Apptimize.getBool(
        'shouldShowAddAccountInAvatar',
        false
      )
        ? 'AddAccountAvatarMenu'
        : 'AddAccountDashboard'
      const abTestingList = [addAccountVariant] // list of A/B Testing feature variants
      abTesting = abTestingList.join('|')
    }

    if (typeof window !== 'undefined') {
      SFDDL.apptimize = {
        subject,
        ...slowRolloutFeatures,
        ...(abTesting && { abTesting })
      }
    }
  }, [
    isApptimizeInitialized,
    setIsFeatureFlagsFetched,
    subject,
    isCreditAccountsAPIError
  ])

  return {
    isApptimizeInitialized
  }
}
